import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useConnection } from './react.js';
const statusColors = {
    connected: '#10b981', // green
    connecting: '#f59e0b', // amber
    disconnected: '#6b7280', // gray
    error: '#ef4444', // red
};
const statusLabels = {
    connected: 'Connected',
    connecting: 'Connecting...',
    disconnected: 'Disconnected',
    error: 'Connection Error',
};
/**
 * Connection status indicator component
 *
 * @example
 * ```tsx
 * // Simple status dot
 * <ConnectionStatus />
 *
 * // With network name
 * <ConnectionStatus showNetwork />
 *
 * // Custom styling
 * <ConnectionStatus
 *   showNetwork
 *   className="my-status"
 *   style={{ fontSize: '12px' }}
 * />
 * ```
 */
export function ConnectionStatus({ showLatency = false, showNetwork = false, className = '', style = {}, inline = true, }) {
    const { state, network } = useConnection();
    const containerStyle = {
        display: inline ? 'inline-flex' : 'flex',
        alignItems: 'center',
        gap: '8px',
        fontSize: '14px',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        ...style,
    };
    const dotStyle = {
        width: '8px',
        height: '8px',
        borderRadius: '50%',
        backgroundColor: statusColors[state] || statusColors.disconnected,
        animation: state === 'connecting' ? 'pulse 1.5s ease-in-out infinite' : undefined,
    };
    const labelStyle = {
        color: statusColors[state] || statusColors.disconnected,
        fontWeight: 500,
    };
    const networkStyle = {
        color: '#9ca3af',
        fontSize: '12px',
    };
    return (_jsxs("div", { className: className, style: containerStyle, children: [_jsx("span", { style: dotStyle }), _jsx("span", { style: labelStyle, children: statusLabels[state] || state }), showNetwork && network && (_jsxs("span", { style: networkStyle, children: ["(", network, ")"] })), _jsx("style", { children: `
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      ` })] }));
}
/**
 * Simple loading spinner
 *
 * @example
 * ```tsx
 * function DataPanel() {
 *   const { data, loading } = useTopology();
 *
 *   if (loading) return <LoadingSpinner />;
 *
 *   return <TopologyGraph data={data} />;
 * }
 * ```
 */
export function LoadingSpinner({ size = 24, color = '#6366f1', className = '', }) {
    const style = {
        width: size,
        height: size,
        border: `2px solid ${color}20`,
        borderTopColor: color,
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
    };
    return (_jsxs(_Fragment, { children: [_jsx("div", { className: className, style: style }), _jsx("style", { children: `
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      ` })] }));
}
/**
 * Error display component
 *
 * @example
 * ```tsx
 * function DataPanel() {
 *   const { data, error, refresh } = useTopology();
 *
 *   if (error) {
 *     return <ErrorDisplay message={error.message} onRetry={refresh} />;
 *   }
 *
 *   return <TopologyGraph data={data} />;
 * }
 * ```
 */
export function ErrorDisplay({ message, onRetry, className = '', }) {
    const containerStyle = {
        padding: '16px',
        backgroundColor: '#fef2f2',
        border: '1px solid #fecaca',
        borderRadius: '8px',
        color: '#991b1b',
        fontFamily: 'system-ui, -apple-system, sans-serif',
    };
    const messageStyle = {
        marginBottom: onRetry ? '12px' : 0,
    };
    const buttonStyle = {
        padding: '8px 16px',
        backgroundColor: '#dc2626',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        fontWeight: 500,
    };
    return (_jsxs("div", { className: className, style: containerStyle, children: [_jsx("p", { style: messageStyle, children: message }), onRetry && (_jsx("button", { style: buttonStyle, onClick: onRetry, children: "Retry" }))] }));
}
/**
 * Container component that handles loading/error states
 *
 * @example
 * ```tsx
 * function TopologyPanel() {
 *   const result = useTopology();
 *
 *   return (
 *     <DataContainer {...result}>
 *       {(topology) => (
 *         <D3Graph
 *           nodes={topology.nodes}
 *           links={topology.links}
 *         />
 *       )}
 *     </DataContainer>
 *   );
 * }
 * ```
 */
export function DataContainer({ data, loading, error, onRetry, loadingComponent = _jsx(LoadingSpinner, {}), errorComponent = (err, retry) => _jsx(ErrorDisplay, { message: err.message, onRetry: retry }), children, }) {
    if (loading) {
        return _jsx(_Fragment, { children: loadingComponent });
    }
    if (error) {
        return _jsx(_Fragment, { children: errorComponent(error, onRetry) });
    }
    if (data === undefined) {
        return null;
    }
    return _jsx(_Fragment, { children: children(data) });
}
//# sourceMappingURL=components.js.map